<script>
export default {
  name: "HomeView",
};
</script>

<script setup>
// La portada.
//
// Figma: Template/Home-Reset, tablero claro 730:7237 (y su gemelo oscuro en
// 659:2861). Prototipo: docs/NEW UI/astra-prototipo-24-08.html, pantalla Home.
//
// Orden de la plantilla: hero · tres puertas · programación de hoy ·
// actualidad. La cabecera y el pie los pinta App.vue para todas las vistas del
// reset, así que aquí no se montan.
//
// El hero NO es el de Radio aunque comparta componente: la pila va SIN la
// portada encajada. Llevó una tercera columna con la ficha del artista —el
// nodo la tiene— y **se retiró el 26.08.26 por decisión de Pablo**.
// `ArtistAside` sigue en el sistema, sin consumidores, por si vuelve con la
// ficha «Con Astra» de `_3`.
//
// ⚠️ La pila se queda en **400**, la medida de siempre. Al retirar la ficha se
// subió a 500 por iniciativa propia y no se había pedido: revertido.
//
// ⚠️ La imagen es la del ARTISTA, no la carátula del álbum. Es lo que ya hacía
// la portada anterior y lo que pide el diseño; la carátula solo aparece como
// dato en el pie de foto. Corregido el 25.08.26 sobre la primera lectura del
// nodo.
//
// ⚠️ La capa de datos NO cambia: mismos composables que Radio. La bio y la
// imagen del artista vienen de la cadena de docs/ARTISTAS_FUENTES.md, no de
// WordPress; de WordPress sale solo la parrilla.
//
// 🚧 En construcción (C4 · Home): H2 deja hero y pie. Las tres puertas, la
// parrilla del día y actualidad llegan en H3 y H4.
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useHead } from "@vueuse/head";
import { storeToRefs } from "pinia";

import { usePlayerStore } from "@/stores/player";
import useNowPlaying from "@/composables/useNowPlaying";
import { useCurrentProgramme } from "@/composables/useCurrentProgramme";
import { getTodayShows, getRadioNowMinutes } from "@/services/daySchedule";
import { getLatestVideo } from "@/services/tvVideos";
import { getEntradas } from "@/services/canalAbierto";
import { getAgenda } from "@/services/conciertos";
import { fetchRadar } from "@/services/api";
import { getAvisosActualidad } from "@/services/avisos";
import { olvidarContenido } from "@/services/refrescoContenido";
import { formatRange } from "@/utils/timeUtils";
import { resetCircuitBreaker } from "@/services/fetchWithRetry";
import { useRadioAudio } from "@/composables/useRadioAudio";
import { useAudioLevels } from "@/composables/useAudioLevels";
import { PLACEHOLDER as astraPlaceHolder } from "@/services/placeholder";

import DuotoneArt from "@/components/ds/DuotoneArt.vue";
import Transport from "@/components/ds/Transport.vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import ChipAction from "@/components/ds/ChipAction.vue";
import HeroAgenda from "@/components/ds/HeroAgenda.vue";
import ShareChip from "@/components/ds/ShareChip.vue";
import DoorCard from "@/components/ds/DoorCard.vue";
import VideoFacade from "@/components/ds/VideoFacade.vue";
import { useVideoExclusivo } from "@/composables/useVideoExclusivo";
import DayBar from "@/components/ds/DayBar.vue";
import DataRow from "@/components/ds/DataRow.vue";
import ModuleTitleBar from "@/components/ds/ModuleTitleBar.vue";
import GridCell from "@/components/ds/GridCell.vue";
import SendRecord from "@/components/ds/SendRecord.vue";
import ArtCarousel from "@/components/ds/ArtCarousel.vue";
import CornerTag from "@/components/ds/CornerTag.vue";
import { LISTEN } from "@/services/host";
import { generosDe } from "@/services/programmes";
import { useFichaArtista } from "@/composables/useFichaArtista";

const STATION_ID = "3";

const playerStore = usePlayerStore();
const { isPlaying } = storeToRefs(playerStore);

// ── El retrato del hero se enciende al dar al play, como en Radio ──
//
// 🔴 **Igualado con Radio el 25.09.26, por encargo de Pablo.** Aquí había una
// excepción: el retrato nacía a color y solo se apagaba después de haber
// sonado alguna vez en la visita (`isPlaying || !haSonado`). La idea, del
// 02.09.26, era que la emisora emite aunque tú no le hayas dado al play, y que
// entrar y ver la foto en gris decía «esto está parado» de algo que sí suena.
//
// El problema es que dejaba a la portada contando otra cosa que Radio: la
// misma emisión, con la misma imagen, en color en un sitio y en gris en el
// otro, sin que nada lo explicara. **En el sistema el color es información, y
// tiene que decir lo mismo en todas partes**: aquí y en Radio significa que la
// emisión está sonando **en este aparato**.
//
// Radio lo ata a `isPlaying` a secas —`NowPlaying` recibe `:playing` y se lo
// pasa a `DuotoneArt` como `:live`—, así que esto hace lo mismo.

// La onda se engancha al MISMO elemento que reproduce la emisión, el de
// PlayerSite: `createMediaElementSource` solo admite una llamada por elemento,
// así que un <audio> propio aquí sonaría dos veces y dejaría a Radio sin onda.
const { audioEl } = useRadioAudio();
const {
  levels,
  analisis: analisisAudio,
  start: startLevels,
  stop: stopLevels,
} = useAudioLevels();

const {
  nowPlaying,
  activeTrack,
  artistImage,
  updateNowPlaying,
  cancelSongEndTimer,
} = useNowPlaying(STATION_ID);

// Qué suena y hasta cuándo. El composable se relee solo al terminar la franja,
// así que el fondo y la parrilla cambian con el programa y no cinco minutos
// después.
const {
  currentShow,
  programme,
  start: startProgramme,
  stop: stopProgramme,
} = useCurrentProgramme(STATION_ID);

const todayShows = ref([]);
const nowMinutes = ref(getRadioNowMinutes());
const latestVideo = ref(null);
const releases = ref([]);
// Lo último que ha entrado por el buzón, para la puerta de Radar.
const ultimaEntrada = ref(null);
const releaseIndex = ref(0);
const noticias = ref([]);

// ── Lo que suena ──
const song = computed(() => activeTrack.value?.song || null);
const artist = computed(() => song.value?.artist || "");

// La ficha del artista. El diálogo lo monta `App.vue`; aquí solo se pide.
//
// ⚠️ Se le pasa `artistImage` como retrato de respaldo: en la portada no hay
// carátula de disco a mano, y sin nada el hueco de la ficha sale gris hasta que
// llegue la foto de verdad —que hoy no tiene el 90 % del catálogo—.
const { abrir: abrirFicha } = useFichaArtista();
const title = computed(() => song.value?.title || "");

// 🔴 Aquí había un `album` que iba de pie sobre la foto del artista, y se
// retira (encargo de Pablo, 27.08.26): la imagen es del ARTISTA, y colgarle
// encima el título del disco la convierte en la portada de otra cosa. El álbum
// no se pierde de la web —sigue en Radio, donde la carátula sí está encajada
// junto a la foto y el pie explica qué se está viendo—.
//
// El kicker de la portada tampoco dice el álbum: dice de qué programa viene lo
// que suena y de qué palo es.
// 🔴 **El género es el del PROGRAMA, y viene del servidor.** Hasta el 22.09.26
// aquí se pintaba `artistFacts.genre`, que es el género del artista que suena y
// **sale de TheAudioDB**, una base externa: cambiaba con cada canción, no lo
// controlamos nadie y decía «Alternative Rock» mientras el programa era Alta
// Fidelidad —Indie, Rock Clásico, New Romantic—. Lo cazó Pablo: «debe mostrar
// los géneros que obtenemos desde el servidor».
//
// ⚠️ El género del artista sigue estando donde le toca, en su ficha de Radio.
// Lo que no puede es hacerse pasar por el del programa.
//
// Los une `generosDe`, que es el único sitio donde se decide cómo se escribe
// una lista de géneros.
// 🔴 **Dos líneas, no una.** Pablo, 22.09.26: arriba el programa y debajo su
// franja y sus géneros. En una sola línea los tres datos se leían como una
// ristra —«ALTA FIDELIDAD · 12–16 · INDIE, ROCK CLÁSICO, NEW ROMANTIC»— y el
// nombre del programa, que es lo que se busca, no destacaba sobre lo demás.
const programaEnAntena = computed(() => programme.value || "");

const franjaYGeneros = computed(() =>
  [currentShow.value?.range && formatRange(currentShow.value.range), generosDe(currentShow.value)]
    .filter(Boolean)
    .join(" · "),
);

// ── El reloj del tema ──
// El servidor solo refresca `elapsed` cuando se le pregunta, cada treinta
// segundos: leerlo directo deja el contador saltando y la barra congelada.
// Se ancla la referencia y cuenta el navegador, volviendo a anclar en cada
// respuesta para no acumular deriva. Igual que en Radio.
const duration = computed(() => Number(nowPlaying.value?.duration) || 0);

const anclaSegundos = ref(0);
const anclaInstante = ref(Date.now());
const ahora = ref(Date.now());
let relojTimer = null;

watch(
  () => nowPlaying.value?.elapsed,
  (v) => {
    anclaSegundos.value = Number(v) || 0;
    anclaInstante.value = Date.now();
    ahora.value = Date.now();
  },
  { immediate: true },
);

const elapsed = computed(() => {
  const corrido =
    anclaSegundos.value + (ahora.value - anclaInstante.value) / 1000;
  return duration.value ? Math.min(corrido, duration.value) : corrido;
});

// ── La parrilla del día ──
//
// La regla de horas y la lista salen de la misma consulta: cada franja pesa lo
// que dura su programa, así que la barra necesita los mismos minutos que las
// filas. Pedirlo dos veces daría dos verdades cuando cambie el programa entre
// una llamada y otra.
const daySlots = computed(() =>
  todayShows.value.map((show) => ({
    // La regla es estrecha: cabe la hora de entrada, no el nombre.
    label: String(Math.floor(show.start)).padStart(2, "0"),
    start: show.startMin,
    end: show.endMin,
    live: show.live,
  })),
);

const dayRows = computed(() =>
  todayShows.value.map((show) => ({
    time: formatRange(show.range),
    name: show.name,
    meta: generosDe(show),
    resident: show.resident,
    tail: show.live ? "En antena" : "",
    tailAccent: show.live,
    live: show.live,
  })),
);

const nowLabel = computed(() => {
  const m = nowMinutes.value % (24 * 60);
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
});

// ── Las tres puertas ──
//
// Una puerta sin datos NO se pinta. Es la regla del sistema para las celdas y
// vale igual aquí: tres puertas de las que una anuncia un vídeo que no existe
// valen menos que dos puertas ciertas.
// 🔴 Los campos son los que normaliza `services/tvVideos.js`, no los del
// contrato: `thumbnail` y no `thumbnail_url`. Desde el 26.08 los vídeos salen
// de `tv.json` del servidor de la radio y no del manager.
//
// `duration` ya no se pide: el contrato lo publica vacío o nulo en los treinta
// y nueve vídeos, así que ese hueco del meta nunca se llenaba.
const tvDoor = computed(() => {
  const v = latestVideo.value;
  if (!v) return null;
  return {
    // Para la salida a la sección: `/butaca/videoclip/<slug>` abre su página.
    // Desde el 21.09.26 la tarjeta ya no carga el vídeo en el hero: cada pieza
    // tiene dirección propia (entrega de Butaca).
    slug: v.slug || "",
    title: v.name || "Última sesión",
    url: v.url || "",
    image: v.thumbnail || "",
    imageFallback: v.thumbnailFallback || "",
    meta: [v.playlist, "Al reproducir, la radio se pausa"].filter(Boolean),
  };
});

// La emisión se para al arrancar un vídeo. No pueden sonar las dos cosas.
// Misma regla que en la sección TV, mismo cable: ver `useVideoExclusivo`.
const videoPuerta = ref(null);
const { alReproducirVideo, alPausarVideo } = useVideoExclusivo(videoPuerta);

// La puerta del buzón tiene dos caras, y la que toca depende de si hay algo
// que enseñar:
//
//   · con entradas → el último disco recibido, con su portada. Es la prueba de
//     que el buzón funciona, que convence más que cualquier frase.
//   · sin entradas → qué es la sección. **No se inventa una entrada** ni se
//     deja la puerta a medias con un hueco de imagen vacío.
const canalDoor = computed(() => {
  const e = ultimaEntrada.value;

  if (!e) {
    return {
      title: "Manda tu disco",
      meta: ["Sin sello, sin nota de prensa", "Lo escuchamos entero"],
      image: "",
      cta: "Cómo mandarlo",
      to: "/radar",
    };
  }

  return {
    title: `${e.artist} · ${e.title}`,
    // Lo que sitúa el disco. Se salta lo que falte: son opcionales de verdad.
    meta: [e.place, e.formatLabel, e.onAir ? "En parrilla" : ""].filter(Boolean),
    image: e.cover,
    // 🔴 A la **ficha del artista**, que es donde Radar enseña hoy lo que llega
    // por el buzón. Apuntaba a `/radar/<slug>`, la vista de detalle por trabajo
    // del diseño anterior, que se quedó sin rehacer cuando el escaparate pasó a
    // ser por artista: desde la portada se aterrizaba en la página vieja.
    cta: "Ver la ficha",
    to: `/radar/artista/${e.slug}`,
  };
});

// La puerta de lanzamientos sale de los posts de la categoría «lanzamientos»
// del manager, que es de donde ya salía el carrusel de la portada anterior.
// `useNewAlbums.js` apunta a la playlist «Nuevas Adiciones» de AzuraCast y no
// lo usa nadie: está vivo pero desconectado. Anotado en REFACTOR.md para
// decidir cuál manda.
//
// Pasan solas, sin flechas ni puntos, y son las que mande el servidor: hoy ocho,
// todo lo publicado en su ventana de 30 días. Antes se recortaban a cuatro y la
// mitad del mes no llegaba a verse.
//
// El texto de la puerta va atado a la que se ve: un título quieto sobre una
// imagen que cambia se lee como un fallo de sincronía, no como un carrusel.
const releaseArt = computed(() =>
  releases.value.map((post) => post.imagen).filter(Boolean),
);

const releaseDoor = computed(() => {
  const post = releases.value[releaseIndex.value] || releases.value[0];
  if (!post) return null;
  return {
    title: post.titulo || "",
    meta: [post.categoria, post.fecha].filter(Boolean),
    to: post.slug ? `/actualidad/${post.slug}` : null,
  };
});

// ── Emisión ──
// El análisis arranca DENTRO del gesto: el AudioContext nace suspendido y solo
// se reanuda en una interacción del usuario.
async function toggle() {
  const wasPlaying = isPlaying.value;
  playerStore.toggle();
  if (wasPlaying) {
    stopLevels();
  } else if (audioEl.value) {
    await startLevels(audioEl.value);
  }
}

// Y si la emisión arranca desde fuera —el play de la cabecera, el del modal—
// la onda se engancha igual.
// 🔴 `immediate`, y vigilando también el elemento.
//
// Sin él, la onda solo arrancaba en el CAMBIO de `isPlaying`. Al dar al play en
// la portada y navegar aquí, la emisión sigue sonando pero `isPlaying` ya era
// `true` y no vuelve a cambiar: el `watch` de la vista recién montada no se
// dispara nunca y la onda se queda **congelada** —reportado por Pablo el
// 26.08.26—. El comentario de arriba decía «manda el estado, no el clic» y era
// justo lo que no pasaba.
//
// Se vigila también `audioEl` porque es un ref de módulo que rellena
// PlayerSite: si por lo que sea aún no está registrado al montar, el análisis
// engancha en cuanto aparezca en vez de perder el arranque.
//
// El grafo se comparte por elemento —`useAudioLevels` lo cachea en un
// WeakMap—, así que reenganchar no crea un segundo `MediaElementSource`, que
// lanzaría y dejaría la radio muda. Y el AudioContext ya está reanudado por el
// gesto de la vista anterior, así que no hace falta otro.
watch(
  [isPlaying, audioEl],
  async ([playing, el]) => {
    if (playing && el) await startLevels(el);
    else if (!playing) stopLevels();
  },
  { immediate: true },
);

// Lo que se dice al compartir. El enlace lo pone el propio popover: en la
// portada es la dirección de la casa, no la de una ficha.
const shareText = computed(() =>
  artist.value ? `${artist.value} · ${title.value}` : "Astra FM",
);

// ── Ciclo ──
let showTimer = null;
let nowTimer = null;

async function loadDay() {
  try {
    todayShows.value = await getTodayShows(null, STATION_ID);
    nowMinutes.value = getRadioNowMinutes();
  } catch (error) {
    // Sin parrilla la sección entera no se pinta: media parrilla engaña más
    // que ninguna.
    todayShows.value = [];
  }
}

async function loadDoors() {
  // En paralelo y con fallos independientes: que TV no responda no puede
  // dejar sin lanzamientos a la portada.
  const [video, lanzamientos, canal] = await Promise.all([
    getLatestVideo(),
    // 🔴 Del endpoint de avisos, y no de un filtro nuestro: el servidor publica
    // los últimos 30 días ya ordenados y filtrados. Antes esto recortaba a
    // siete días sobre `actualidad.json` entero, y con esa ventana la puerta
    // llegó a quedarse con un solo disco.
    getAvisosActualidad().catch(() => []),
    getEntradas().catch(() => []),
  ]);
  latestVideo.value = video;
  // 🔴 **Solo lanzamientos.** `?fuente=actualidad` trae todos los posts de
  // Actualidad —lanzamientos, noticias e historias—, y la puerta es «Nuevas
  // realizaciones»: el 04.10.26 se coló la historia de Córdoba entre los discos
  // (lo vio Pablo). Se filtra por el `tipo` del aviso, que es el del contrato.
  releases.value = (lanzamientos || []).filter((a) => a.tipo === "lanzamiento");
  ultimaEntrada.value = (canal || [])[0] || null;
}

// La API devuelve `categorias` —un array de objetos con id, nombre y slug— y no
// un `categoria` de texto. Leerlo como cadena dejaba el kicker vacío en el
// destacado y en las tres breves, sin dar ningún error.
//
// `imagen` es la apaisada del servidor —800×600, unos 80 KB—, ya lista para el
// hueco. Antes era el archivo completo de WordPress y había que derivarla.
function normalizaPost(post) {
  if (!post) return null;
  return {
    ...post,
    categoria: post.categorias?.[0]?.nombre || "",
  };
}

// ── La agenda del hero ──
//
// Contrato de conciertos del 01.09.26. Vacía es una respuesta normal —«la agenda
// puede estar vacía o casi al principio»—, y entonces el hero se queda con la
// emisión a todo lo ancho, que es lo que había hasta hoy.
const agenda = ref([]);

async function loadAgenda() {
  agenda.value = await getAgenda();
}

// Los tipos que van en el módulo Actualidad de la portada.
const TIPOS_ACTUALIDAD = ["noticias", "historias"];

async function loadNews() {
  // Cuatro iguales, en orden. Antes se partían en destacado + tres breves; el
  // reparto se fue el 18.09.26.
  //
  // 🔴 **Noticias e historias, lo último primero, de izquierda a derecha**
  // (Pablo, 04.10.26). Hasta ese día solo se pedían noticias, y una historia
  // como la de Córdoba no llegaba nunca a la portada. Los lanzamientos no van
  // aquí: tienen su puerta, «Nuevas realizaciones».
  //
  // El orden es el de `getActualidad`, del más nuevo al más viejo por fecha y
  // hora de publicación; aquí solo se filtra y se cortan cuatro.
  const posts = await fetchRadar(0).catch(() => []);
  noticias.value = posts
    .filter((p) => TIPOS_ACTUALIDAD.includes(p.categorias?.[0]?.slug))
    .slice(0, 4)
    .map(normalizaPost)
    .filter(Boolean);
}

// Los cortacircuitos se resetean al montar. `fetchWithRetry` bloquea un host
// tras varios fallos seguidos; si se llega a la portada con el circuito
// abierto, la vista se queda en esqueleto para siempre y nada lo explica.
function unblock() {
  resetCircuitBreaker(`${LISTEN}`);
  resetCircuitBreaker("https://astra.fm");
  resetCircuitBreaker("https://www.theaudiodb.com");
}

function startClock() {
  if (relojTimer) return;
  relojTimer = setInterval(() => {
    ahora.value = Date.now();
    // La aguja de la regla también corre. En minutos, así que el cálculo solo
    // cambia de verdad una vez por minuto.
    nowMinutes.value = getRadioNowMinutes();
  }, 1000);
}

function startPolling() {
  stopPolling();
  startClock();
  nowTimer = setInterval(() => updateNowPlaying?.(), 30 * 1000);
  // El programa lo lleva su composable, que se relee al terminar la franja. La
  // parrilla se queda con su red de seguridad: sus filas solo cambian si
  // alguien edita la programación en WordPress.
  showTimer = setInterval(loadDay, 5 * 60 * 1000);
}

function stopPolling() {
  [nowTimer, showTimer, relojTimer].forEach((t) => t && clearInterval(t));
  nowTimer = showTimer = relojTimer = null;
}

// En segundo plano no se pide nada: la pestaña oculta no la mira nadie y el
// navegador estrangula los timers igualmente.
function onVisibility() {
  if (document.hidden) {
    stopProgramme();
    stopPolling();
    return;
  }
  unblock();
  updateNowPlaying?.();
  // Volver de segundo plano es el caso en el que más fácil es que el programa
  // haya cambiado: los temporizadores estaban parados. Se relee entero.
  startProgramme();
  loadDay();
  startPolling();

  // 🔴 Y lo mismo vale para lo que publica la redacción.
  //
  // Las puertas y la actualidad se pedían UNA vez, al montar, y nunca más:
  // medido el 27.08 con la portada abierta y quieta, una sola petición a cada
  // JSON en tres minutos. Con `keep-alive` la vista tampoco se remonta al
  // volver de otra sección, así que quien dejaba la web abierta no veía un
  // artículo nuevo jamás — había que recargar, y ni recargando, porque el
  // navegador contestaba de su caché.
  //
  // Volver a la pestaña es el momento honesto para preguntar: ya existe el
  // aviso, no cuesta un sondeo periódico y es cuando más probable es que algo
  // haya cambiado. Se olvida lo guardado ANTES de pedir, o se volvería a
  // servir lo mismo desde la memoria del módulo.
  //
  // No bloquea nada: la portada sigue pintada con lo que ya tenía y solo se
  // repinta lo que de verdad llegue distinto.
  olvidarContenido();
  loadDoors();
  loadNews();
  loadAgenda();
}

onMounted(async () => {
  startClock();
  unblock();
  document.addEventListener("visibilitychange", onVisibility);
  // Lo de arriba primero. Las puertas y la actualidad están por debajo del
  // pliegue y pueden llegar después; el hero no.
  startProgramme();

  // 🔴 Pero «después» no es «cuando termine el hero». Estaban DEBAJO del
  // `await`, así que no salían a pedir sus datos hasta que el now playing
  // resolviera: medido el 26.08, unos veinte segundos con la portada a medias
  // —sin puertas y sin actualidad— aunque su JSON estuviera a mano. Se lanzan
  // ya, sin esperar; cada una pinta lo suyo cuando llega.
  loadDoors();
  loadNews();
  // La agenda comparte fila con el hero, así que se pide con ellos y no con lo
  // de abajo; pero tampoco espera al now playing: si tarda, el hero se pinta
  // solo con la emisión y la agenda entra cuando llega.
  loadAgenda();

  await Promise.all([updateNowPlaying?.(), loadDay()]);
  startPolling();
});

onUnmounted(() => {
  document.removeEventListener("visibilitychange", onVisibility);
  stopProgramme();
  stopPolling();
  stopLevels();
  cancelSongEndTimer?.();
});

useHead({
  title: "Astra FM · radio independiente",
  meta: [
    {
      name: "description",
      content:
        "Radio independiente. Rock y lo que se le parece, veinticuatro horas, sin listas compradas.",
    },
  ],
});
</script>

<template>
  <div class="home-view">
    <!-- 1 · el hero: lo que suena y lo que viene ────────────────────────
         Figma `hero 1380:181`. Dos mitades: el cuadrado de emisión —foto,
         velo y la banda encima— y la agenda de conciertos.

         🔴 **Esto ya no es `NowPlaying`.** Aquel bloque pone el texto AL LADO
         de la foto, y el hero nuevo lo pone ENCIMA, dentro del cuadrado: es
         otra pieza, no otra variante. `NowPlaying` sigue intacto en Radio, que
         es de donde salió y donde la ficha del artista sí acompaña a la
         imagen.

         El velo y la franja son las dos maneras de escribir sobre una imagen
         que documenta `DESIGN.md`, y aquí conviven a propósito: la foto del
         artista es nuestra y admite velo; el cartel del concierto lo entrega
         quien promociona y solo admite franja. -->
    <section class="hero" :class="{ 'hero--solo': !agenda.length }">
      <div class="hero__emision">
        <DuotoneArt
          class="hero__retrato"
          :src="artistImage || astraPlaceHolder"
          :alt="artist"
          :live="isPlaying"
          :hover-color="false"
          tono="emision"
          ratio="1"
        />

        <!-- ── La trama de señal ──
             `Portada Astra v3`, `.cover__scan`: líneas de 1 px cada 3, negro al
             22%, fundidas en `multiply` al 45%. El refresco de un tubo.

             🔴 **No es un filtro de la imagen, es una capa encima.** Un filtro
             procesa la foto; esto la cruza, que es lo que hace que se lea como
             señal y no como una foto retocada. Por eso va aquí y no en
             `DuotoneArt`: el gris es del sistema y lo lleva la imagen a todas
             partes; esta trama es del cuadrado de emisión y de ningún sitio más.

             ⚠️ Y va **debajo del velo**, no encima: el velo funde la foto con el
             lienzo al pie, y tiene que fundir también las líneas o se verían
             cruzando el nombre del artista. -->
        <div class="hero__trama" aria-hidden="true" />

        <!-- El velo, no una caja: arranca 56 px por encima del primer renglón
             y funde hasta `bg/canvas`. Sin filete y sin fondo propio, porque
             no es un bloque sobre la foto, es parte de ella. -->
        <div class="hero__velo" aria-hidden="true" />

        <div class="hero__banda">
          <div class="hero__kicker">
            <ChipAction label="En emisión" variant="solid-accent" show-dot />
            <span
              v-if="programaEnAntena || franjaYGeneros"
              class="hero__programa"
            >
              <span v-if="programaEnAntena" class="hero__programa-nombre">{{
                programaEnAntena
              }}</span>
              <span v-if="franjaYGeneros" class="hero__programa-detalle">{{
                franjaYGeneros
              }}</span>
            </span>
          </div>

          <!-- 🔴 El nombre abre la ficha del artista (24.09.26). Un botón y no
               un enlace: el artista de rotación no tiene página, lo que hay es
               una ficha que se abre encima sin sacar a nadie de la emisión. -->
          <h2 class="hero__artista">
            <button
              v-if="artist"
              type="button"
              class="hero__artista-boton"
              @click="abrirFicha(artist, artistImage)"
            >
              {{ artist }}
            </button>
          </h2>
          <p v-if="title" class="hero__tema">{{ title }}</p>

          <Transport
            class="hero__transport"
            sobre-imagen
            :levels="levels"
            :analisis="analisisAudio"
            :playing="isPlaying"
            :elapsed="elapsed"
            :duration="duration"
            :loading="!song"
            @toggle="toggle"
          />

          <!-- Solo compartir. «Ver artista» e «Historial» se retiran por
               encargo de Pablo (26.08.26): la ficha de artista es V1 y el
               historial ya tiene su módulo propio más abajo, así que eran dos
               salidas del hero que no llevaban a nada mejor de lo que ya hay
               en la página. El nodo los sigue dibujando y aquí no se
               reponen. -->
          <div class="hero__acciones">
            <ShareChip :text="shareText" surface="imagen" />
          </div>
        </div>
      </div>

      <HeroAgenda v-if="agenda.length" :conciertos="agenda" />
    </section>

    <!-- 2 · las tres puertas -->
    <section
      v-if="tvDoor || releaseDoor"
      class="home-view__doors"
      aria-label="Secciones destacadas"
    >
      <!-- 🔴 **Sin `to`.** El contrato del servidor lo prohíbe expresamente:
           «en la HOME el click NUNCA navega a la sección TV». La tarjeta no es
           un enlace — el vídeo se reproduce aquí mismo y se amplía desde el
           propio reproductor. El enlace a la sección vive en el menú. -->
      <!-- La salida a la sección va FUERA de la tarjeta, y por dos razones.
           La primera es de marcado: esta puerta se renderiza como `<button>`
           —no navega, reproduce— y un enlace dentro de un botón es contenido
           interactivo anidado. La segunda es de comportamiento: siendo hermano
           no le quita ni un toque al play, así que el vídeo se sigue
           reproduciendo AQUÍ, que es lo que exige el contrato. Lo que lleva a
           la sección es este enlace, y solo él. -->
      <div v-if="tvDoor" class="home-view__tv">
        <DoorCard
          badge="Último videoclip"
          :title="tvDoor.title"
          :meta="tvDoor.meta"
        >
          <template #art>
            <VideoFacade
              ref="videoPuerta"
              :url="tvDoor.url"
              :poster="tvDoor.image"
              :poster-fallback="tvDoor.imageFallback"
              :title="tvDoor.title"
              tag="Último videoclip"
              hover-color
              @play="alReproducirVideo"
              @pause="alPausarVideo"
            />
          </template>
        </DoorCard>

        <!-- La misma llamada que la puerta, con el botón del sistema: aquí
             vivía una copia de su CSS, y era uno de los cinco sitios donde
             había que repetir cada cambio. -->
        <ButtonDs
          v-if="tvDoor.slug"
          class="home-view__tv-cta"
          label="Ver Butaca"
          variant="quiet"
          size="micro"
          :to="`/butaca/videoclip/${tvDoor.slug}`"
        />
      </div>

      <DoorCard
        v-if="releaseDoor"
        badge="Nuevas realizaciones"
        :title="releaseDoor.title"
        :meta="releaseDoor.meta"
        cta="Ver lanzamientos"
        :to="releaseDoor.to || '/actualidad'"
      >
        <template #art>
          <ArtCarousel
            :images="releaseArt"
            @change="releaseIndex = $event"
          />
          <CornerTag anchor="top">Nuevas realizaciones</CornerTag>
        </template>
      </DoorCard>

      <!-- **Radar** —«Canal abierto» hasta el 31.08.26—. Enseña lo ÚLTIMO que
           ha entrado por el buzón, y
           mientras no haya nada publicado dice lo que la sección es. Nunca un
           disco inventado: por eso se fue `src/data/demos.js`, una de las tres
           deudas que impedían desplegar. Ver docs/CANAL_ABIERTO.md. -->
      <DoorCard
        badge="Radar"
        :title="canalDoor.title"
        :meta="canalDoor.meta"
        :image="canalDoor.image"
        :cta="canalDoor.cta"
        :to="canalDoor.to"
      />
    </section>

    <!-- 3 · la parrilla del día -->
    <section v-if="dayRows.length" aria-label="Programación de hoy">
      <ModuleTitleBar
        title="Programación de hoy"
        end="action"
        action-text="Ver semana completa"
        action-to="/radio/shows"
      />

      <DayBar :slots="daySlots" :now="nowMinutes" :now-label="nowLabel" />

      <DataRow
        v-for="row in dayRows"
        :key="row.time + row.name"
        variant="sched"
        v-bind="row"
      />
    </section>

    <!-- 4 · actualidad
         Barra de título y cuatro celdas en fila, como «Programación de hoy»
         justo encima. Entrega
         `docs/entregas/2026-09-18-actualidad-en-la-portada.md`: era el único
         módulo de la portada que no decía cómo se llamaba, y la portada no
         tenía puerta al archivo de noticias. -->
    <section v-if="noticias.length" aria-label="Actualidad">
      <ModuleTitleBar
        title="Actualidad"
        end="action"
        action-text="Ver todo"
        action-to="/actualidad"
      />

      <div class="home-view__noticias">
        <GridCell
          v-for="post in noticias"
          :key="post.slug"
          :kicker="post.categoria"
          :kicker-end="post.fechaCorta"
          :title="post.titulo"
          :image="post.imagen"
          :to="`/actualidad/${post.slug}`"
        />
      </div>
    </section>

    <!-- 5 · el buzón, el único bloque invertido de la página.
         Va aquí —debajo de actualidad y antes del pie—, que es donde lo pone
         `Template/Home-Reset`. Estuvo un día en Radio por decisión de Pablo y
         vuelve a la portada el 26.08.26.

         Textos fijos a propósito: no hay formulario ni fuente detrás, y la
         llamada es un `mailto:` hasta que lo haya. Es contenido de la casa, no
         un dato inventado: aquí no hay nada que pueda leerse como información
         falsa. -->
    <!-- Sin props: el texto del buzón vive en el componente, porque es el
         mismo aquí y en «Sobre Astra FM». -->
    <!-- Adelanto: la llamada lleva a la sección, que es donde se explica
         y donde está el formulario. El `mailto` vive allí. -->
    <SendRecord cta="Cómo mandarlo" cta-to="/radar" />

  </div>
</template>

<style scoped lang="scss">
@use "@/assets/scss/estructura" as e;
@use "@/assets/scss/type" as t;

.home-view {
  // 🔴 **Las juntas entre módulos las pone la pila, no cada módulo.**
  // `scss/_estructura.scss`. Con esto ningún módulo lleva filete de cierre: si
  // hay dos piezas hay una línea, y si no la hay, no.
  @include e.pila;
  background: var(--color-bg-canvas);
}

// ── Las tres puertas ──
// Filete entre ellas y ninguno al final: el borde del módulo lo pone la línea
// inferior de la sección.
// ══ El hero ══════════════════════════════════════════════════════════════
//
// Figma `hero 1380:181`: 1440 × 530, con el cuadrado de emisión a 530 y la
// agenda ocupando los 910 restantes. El cuadrado es quien pone la altura de la
// fila —es cuadrado, y su lado es el ancho de su columna—, así que el hero no
// lleva alto propio: si la columna encoge, encoge la fila entera y la agenda
// con ella.
.hero {
  display: grid;
  // ── El ancho del cuadrado lo decide el cartel ──
  //
  // 🔴 **Encargo de Pablo, 20.09.26: fuera el tope de 530.** Antes esto era
  // `clamp(340px, 36.8vw, 530px)`, con el tope puesto a propósito —«un cuadrado
  // de 700 en una pantalla ancha sería otra portada»—. El efecto secundario lo
  // vio él mismo: como el cuadrado marca el alto de la fila y ahí se paraba, **el
  // alto del cartel se congelaba en 436 mientras su ancho seguía creciendo**, así
  // que la caja pasaba de 2,09 a 1440 a 3,19 a 1920 y el cartel salía cortado.
  //
  // El cartel se hace a una medida fija, así que **manda él y la caja se adapta**:
  //
  //   hueco    ancho = Wc − s      alto = s − 94   (2 de regla + 92 de franja)
  //   se quiere  ancho / alto = R = 910 / 436       ← la caja del nodo a 1440
  //   despejando s = (Wc + 94R) / (1 + R) = 0,323923·Wc + 63,551
  //
  // ⚠️ **R vuelve a ser la del nodo el 21.09.26, y esta es la historia.** Estuvo
  // un día en 800/355, la medida de los carteles que llegaban por
  // `conciertos.json`. Resulta que **aquello era un error del servidor**: ese
  // campo es la foto de la ficha del artista y alguien subía ahí el cartel de la
  // fecha, así que el cartel se volvía la foto oficial del grupo. Lo han
  // retirado, y **Pablo ha decidido que el cartel no se relaciona con el
  // artista**: caduca con su fecha. Por aquí llegan **retratos**, y medidos hoy
  // son cuadrados. Con la caja a 2,2535 un retrato perdía más de la mitad del
  // alto.
  //
  // A 1440 vuelve a dar 530 exactos, que es la composición del nodo.
  //
  // En porcentaje del propio hero y **no en `vw`**: `vw` incluye la barra de
  // desplazamiento y desviaría el cuadrado unos 15 px, que es justo lo que
  // volvería a descuadrar el cartel. El `max` conserva el suelo de 340, donde el
  // nombre del artista deja de caber en dos líneas.
  //
  grid-template-columns: max(340px, calc(32.3923% + 63.551px)) 1fr;
  background: var(--color-bg-canvas);
  // Sin cierre propio: la junta con el módulo de abajo la pone la pila de la
  // vista (`scss/_estructura.scss`). Dibujarla aquí la duplicaba.
}

// Sin agenda —el buzón de conciertos puede estar vacío— el cuadrado se queda
// solo y el hero vuelve a ser una sola columna. No se estira la foto a 1440:
// una foto de artista recortada a 2,7:1 no es un hero, es una banda.
.hero--solo {
  grid-template-columns: clamp(340px, 36.8vw, 530px);
}

// ⛔ **Sin `overflow: hidden`.** Lo llevaba para recortar el cuadrado, y no hacía
// falta: `DuotoneArt` ya recorta su propia imagen, y el velo y la trama son
// `inset: 0`, así que no se salen. Lo único que recortaba era **el desplegable
// de compartir**, que se abre hacia abajo desde un botón que está al pie: se
// veía asomar media fila por debajo del hero y el resto desaparecía. Lo cazó
// Pablo el 02.09.26.
.hero__emision {
  position: relative;
  min-width: 0;
  background: var(--color-art-mid);
  border-right: 1px solid var(--color-border-default);
}

.hero--solo .hero__emision {
  border-right: 0;
}

.hero__retrato {
  width: 100%;
}

.hero__trama {
  position: absolute;
  inset: 0;
  pointer-events: none;
  mix-blend-mode: multiply;
  opacity: 0.45;
  background-image: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.22) 0 1px,
    transparent 1px 3px
  );
}

// 🔴 **El velo es una meseta, y es el mismo en los dos temas.**
//
// Entrega `docs/entregas/2026-09-18-el-velo-del-hero-pasa-a-meseta.md` § 1.
// Antes iba de transparente a `bg/canvas`, o sea una recta, y **daba su ayuda
// más floja justo donde está el renglón más grande**: el nombre empieza en 300
// y el velo arrancaba en 226, así que cuando llega la letra el degradado lleva
// menos de un cuarto de recorrido. Medido: el peor renglón daba 1,0:1.
//
// **Funde hacia `bg/ink`, no hacia `bg/canvas`.** `bg/ink` es uno de los tres
// tokens que NO se invierten, así que **hay un solo velo para los dos temas** y
// con esto se retira «en claro no hay velo» del 02.09.26. El motivo de aquella
// retirada seguía siendo bueno —fundir hacia papel lavaba tres cuartas partes
// de la foto— y por eso ahora se funde hacia tinta, que apaga sin borrar.
//
// **La subida es una curva, no una recta**: una rampa lineal deja dos quiebros
// —donde arranca y donde alcanza la meseta— y el ojo los lee como dos líneas.
// La curva sale de cero y llega a la meseta con pendiente cero,
// `alfa = 0,72 · s²` con `s = 3t² − 2t³`, en las doce paradas de la entrega.
//
// El 50,94 % son los 270 de la banda: ahí acaba la subida y empieza la meseta.
// Todo en porcentaje, no en píxeles, porque el cuadrado escala con la columna.
.hero__velo {
  position: absolute;
  inset: 22% 0 0;
  background: linear-gradient(
    to bottom,
    // 🔴 **La primera parada va en `transparent` a pelo, no en un `color-mix`
    // al 0 %.** El minificador de producción escribe `0` sin unidad, y dentro de
    // `color-mix` eso no es válido: el navegador tira **todo** el degradado y el
    // velo desaparecía. En local no se minifica, así que solo se veía en
    // producción. Encontrado el 18.09.26 leyendo el CSS publicado.
    transparent 0%,
    color-mix(in srgb, var(--color-bg-ink) 0.1%, transparent) 3.66%,
    color-mix(in srgb, var(--color-bg-ink) 0.8%, transparent) 7.33%,
    color-mix(in srgb, var(--color-bg-ink) 3.4%, transparent) 10.99%,
    color-mix(in srgb, var(--color-bg-ink) 8.9%, transparent) 14.66%,
    color-mix(in srgb, var(--color-bg-ink) 18%, transparent) 18.32%,
    color-mix(in srgb, var(--color-bg-ink) 30.2%, transparent) 21.99%,
    color-mix(in srgb, var(--color-bg-ink) 44.3%, transparent) 25.65%,
    color-mix(in srgb, var(--color-bg-ink) 57.8%, transparent) 29.32%,
    color-mix(in srgb, var(--color-bg-ink) 68%, transparent) 32.98%,
    color-mix(in srgb, var(--color-bg-ink) 72%, transparent) 37.1%,
    color-mix(in srgb, var(--color-bg-ink) 72%, transparent) 100%
  );
  pointer-events: none;
}

// La banda: 26 de sangre a cada lado sobre los 530 del nodo, es decir el 4,9%.
//
// ── La tinta de la banda ──
//
// Entrega del 18.09.26 § 2: **los cuatro renglones van a `text/on-ink`, en los
// dos temas.** La banda ya no se apoya en una foto: se apoya en una meseta de
// tinta, y el texto de esa superficie es el suyo.
//
// 🔴 Aquí vivía un **remapeo de tokens en el ámbito de la banda** —`text/primary`
// y `text/secondary` a `text/inverse`, solo en claro—, que era la primera vez
// que el repo redefinía tokens en un ámbito. Se retira: dejaba fuera
// `text/tertiary`, que es donde vivía el reloj del transporte, justo el renglón
// que peor estaba, y obligaba a volver aquí cada vez que entrara otro
// componente. Con la meseta, cada pieza dice su color y no hace falta.
//
// ⚠️ **Lo que sí conserva el ámbito es la fila de acciones.** El chip de
// «Compartir» trae `text/primary` y `border/strong`, que en claro son casi
// negros, y sobre la meseta no se leerían. Diseño preguntó si se unifica su
// filete o se deja: aquí queda en una sola línea, la de la tinta, que es lo que
// hace la superficie con todo lo que se apoya en ella.
.hero__banda {
  position: absolute;
  left: 4.9%;
  right: 4.9%;
  bottom: 2.6%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.hero__kicker {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  width: 100%;
}

// Dos renglones: el nombre del programa arriba y su franja con sus géneros
// debajo. El chip queda centrado contra los dos, que es donde estaba contra uno.
.hero__programa {
  @include t.mono-label;
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  color: var(--color-text-on-ink);
  text-transform: uppercase;
}

// 🔴 El segundo renglón baja un punto de contraste: es el dato de apoyo, y así
// el nombre del programa se lee primero sin cambiarle el cuerpo a ninguno.
// ⚠️ `text/secondary` no vale aquí: esto va sobre una fotografía, no sobre el
// lienzo, y ahí solo hay dos tonos legibles.
.hero__programa-detalle {
  opacity: 0.72;
}

// 58, no los 70 de `display/hero`: el nombre ya no tiene 800 px de ancho sino
// los 478 de la banda, y a 70 se partía en tres líneas.
// 🔴 **A talla de sección desde el 24.09.26**, no a la del título de página.
// Iba a 58 y tapaba la foto; la regla nueva la escribe diseño en
// `docs/entregas/2026-09-24-nombre-del-artista-en-emision.md`.
//
// ⚠️ **La banda no hay que tocarla**: está anclada abajo con `bottom: 2.6%` y su
// alto sale del contenido, así que al encoger el nombre el aire que sobra queda
// **encima**, sobre la foto, que es justo lo que pide la entrega. El transporte
// y las acciones no se mueven respecto al canto de abajo.
// El botón hereda el titular entero: no se disfraza de botón, solo se marca en
// acento al pasar por encima. Sobre una fotografía, el acento es lo único que se
// lee sin ensuciar la imagen.
.hero__artista-boton {
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:hover,
  &:focus-visible {
    color: var(--color-text-accent);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

.hero__artista {
  @include t.display-nombre-en-antena;
  margin: 0;
  width: 100%;
  color: var(--color-text-on-ink);
}

.hero__tema {
  @include t.mono-lead;
  margin: 0;
  width: 100%;
  color: var(--color-text-on-ink);
}

// El `aire` de 10 del nodo, que separa la ficha del mando. Va como margen y no
// como un hueco vacío en el marcado.
// 🔴 **Vuelve a tener caja**, y con ella se deroga «sin caja» del 02.09.26.
// Entonces el filete iba en `border/default` y recortaba un rectángulo contra
// la foto; ahora la barra no se apoya en la foto, se apoya en la meseta de
// tinta, y su línea es la de esa superficie. El tratamiento entero lo trae la
// variante `sobre-imagen` del componente, que es la misma que usa Radio.
.hero .hero__transport {
  width: 100%;
  margin-top: 10px;
}

.hero__acciones {
  // El chip dice su propio color con `surface="imagen"`: aquí no se remapea
  // nada. Era la última pieza que se pintaba desde fuera.
  display: flex;
  align-items: flex-start;

  // Los chips contiguos comparten borde.
  > :not(:last-child) {
    margin-right: -1px;
  }
}

// 🔴 **La misma llamada que las puertas.** Encargo de Pablo, 02.09.26: que el
// botón del hero se lea igual que el «Ver en TV» de la puerta de abajo, o sea
// `mono/micro` con el aire de `home-view__tv-cta`, y no el chip de 12 px del
// sistema, que al lado se veía de otra familia.
//
// El borde y la tinta ya los hereda de la banda: sobre foto van claros en los
// dos temas.
.hero__acciones :deep(.chip) {
  @include t.mono-micro;
  padding: 7px 13px;
}

// ── Estrechado ──
//
// ⚠️ `Móvil/Home 812:3800` **no está actualizado** con este hero —lo dice el
// changelog del 01.09.26—, así que a 880 no hay nodo que copiar. Se apila, que
// es lo que hace el resto de la web: el cuadrado a todo el ancho con su velo y
// su banda, y la agenda debajo. No se inventa nada nuevo, solo se pone en
// columna lo que a 1440 va en fila.
@media (max-width: 880px) {
  .hero,
  .hero--solo {
    grid-template-columns: 1fr;
  }

  // Los dos renglones son a propósito, pero **cada uno cabe en una línea o se
  // corta**: a 390, con el chip delante, la lista de tres géneros se partía en
  // dos y el bloque crecía hasta empujar el nombre del artista.
  .hero__programa-nombre,
  .hero__programa-detalle {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .hero__emision {
    border-right: 0;
    border-bottom: 1px solid var(--color-border-default);
  }
}

.home-view__doors {
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  > * {
    border-right: 1px solid var(--color-border-default);

    &:last-child {
      border-right: 0;
    }
  }
}

// ── Actualidad ──
//
// Cuatro celdas en fila, con la misma gramática de filetes que el resto de las
// retículas de la casa: cada celda dibuja el suyo a la derecha y el módulo
// cierra abajo con la línea fuerte. El `padding-left` de 1 evita la línea doble
// contra el borde de la página.
//
// 🔴 **Sin escalón de tres columnas.** Con cuatro piezas, tres columnas dejan
// una segunda fila con una celda suelta y dos huecos. Decidido con diseño el
// 18.09.26: de cuatro se pasa a dos.
.home-view__noticias {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  @include e.juntas-de-rejilla;

  > * {
    border-right: 1px solid var(--color-border-default);

    &:last-child {
      border-right: 0;
    }
  }
}

@media (max-width: 1080px) {
  .home-view__noticias {
    grid-template-columns: repeat(2, 1fr);
  }

  // A dos columnas la segunda de cada fila se queda sin vecina a la derecha.
  .home-view__noticias > *:nth-child(2n) {
    border-right: 0;
  }
}

@media (max-width: 720px) {
  .home-view__noticias {
    grid-template-columns: 1fr;
  }

  .home-view__noticias > * {
    border-right: 0;
  }
}

// ── La puerta de TV y su salida ──
//
// La tarjeta se queda con todo el alto y el enlace se descuelga debajo, en el
// margen del cuerpo, así que cae en la esquina inferior derecha de la puerta
// sin taparle nada.
.home-view__tv {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .door {
    flex: 1;
  }
}

// La misma llamada que la de las otras dos puertas, hasta el hover que la
// invierte. Los valores se repiten porque `.door__cta` vive en el `<style
// scoped>` de `DoorCard` y no alcanza hasta aquí, y este enlace tiene que estar
// fuera de la tarjeta. Si cambia una, cambian las dos.
//
// Cuelga del mismo sitio que las otras dos, a la izquierda: el margen lateral
// repite el relleno del cuerpo de la tarjeta, así que las tres llamadas de la
// fila arrancan en la misma vertical.
.home-view__tv-cta {
  align-self: flex-start;
  margin: 0 24px 22px;
}

// Se enciende al pasar por la PUERTA entera, no solo por encima del enlace.
//
// Lo pidió Pablo el 28.08.26, y con razón: las otras dos puertas invierten su
// llamada en cuanto el ratón entra en la tarjeta, y esta se quedaba apagada al
// lado. Que sean el mismo componente y se comporten distinto es justo lo que
// se nota.
//
// El reparo que tenía —encenderlo sobre el vídeo promete que la tarjeta lleva
// a la sección— lo resuelve el propio vídeo: pulsarlo reproduce ahí mismo, y eso
// se ve al instante. Lo que no se explicaba era la diferencia.
.home-view__tv:hover .home-view__tv-cta,
.home-view__tv:focus-within .home-view__tv-cta,
.home-view__tv-cta:hover,
.home-view__tv-cta:focus-visible {
  // El hover de la puerta enciende su llamada, con el mismo rojo que el resto
  // de botones de la web.
  border-color: var(--color-text-accent);
  color: var(--color-text-accent);
}

// Por debajo de la retícula ancha las puertas se apilan. El filete que
// separaba columnas pasa a separar filas.
@media (max-width: 980px) {
  .home-view__doors {
    grid-template-columns: 1fr;
  }

  .home-view__doors > * {
    border-right: 0;
    border-bottom: 1px solid var(--color-border-default);
  }
}

</style>
